import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Star, Quote } from "lucide-react";
import { Hero3D } from "../components/Hero3D";
import { TiltCard } from "../components/TiltCard";
import { SERVICES, TESTIMONIALS, STATS } from "../data/mock";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#FDFBF7]">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[55%] opacity-20 lg:opacity-90 pointer-events-none lg:pointer-events-auto">
          <Hero3D />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-5">Digital Agency · India</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-light tracking-tight leading-[1.02] text-[#1A1014]">
              Grow your business <span className="italic text-[#6C0B1B]">online.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C4B51] leading-relaxed max-w-lg">
              In the world of digital marketing, innovation isn't an option — it's your competitive edge. Digital solutions crafted for small and medium businesses.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-get-started-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#6C0B1B] text-white px-7 py-3.5 font-medium hover:bg-[#8C1126] transition-colors duration-200">
                Get Started <ArrowUpRight size={17} />
              </Link>
              <Link to="/services" data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#6C0B1B]/25 text-[#6C0B1B] px-7 py-3.5 font-medium hover:bg-[#6C0B1B]/5 transition-colors duration-200">
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#6C0B1B] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`stat-${i}`}>
              <p className="font-heading text-5xl font-light">{s.value}</p>
              <p className="mt-1.5 text-sm text-white/65 tracking-wide">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services teaser */}
      <section className="bg-[#F3EFE9] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">What we do</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1014] max-w-md">Tailored digital solutions</h2>
            </div>
            <Link to="/services" data-testid="home-all-services-link" className="inline-flex items-center gap-1.5 text-[#6C0B1B] font-medium hover:gap-3 transition-[gap] duration-200">
              All services <ArrowRight size={17} />
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.slice(0, 3).map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <TiltCard testId={`home-service-card-${i}`} className="group bg-[#FDFBF7] border border-[#6C0B1B]/10 rounded-2xl p-8 h-full shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
                  <div className="w-12 h-12 rounded-xl bg-[#6C0B1B]/8 border border-[#6C0B1B]/15 flex items-center justify-center text-[#6C0B1B] group-hover:bg-[#6C0B1B] group-hover:text-white transition-colors duration-300">
                    <s.icon size={22} />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-semibold text-[#1A1014]">{s.title}</h3>
                  <p className="mt-1 text-sm font-medium text-[#A31621]">{s.tagline}</p>
                  <p className="mt-3 text-[15px] text-[#5C4B51] leading-relaxed">{s.description}</p>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="bg-[#FDFBF7] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp} className="relative">
            <img
              src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwYWdlbmN5JTIwbWFya2V0aW5nJTIwdGVhbXxlbnwwfHx8fDE3ODc5MTI3OTF8MA&ixlib=rb-4.1.0&q=85"
              alt="Levaze team collaborating" className="rounded-2xl w-full h-[420px] object-cover shadow-[0_30px_60px_-20px_rgba(108,11,27,0.25)]" />
            <div className="absolute -bottom-6 -right-4 sm:right-8 bg-[#6C0B1B] text-white rounded-2xl px-6 py-5 shadow-xl">
              <p className="font-heading text-3xl font-light">3x</p>
              <p className="text-xs text-white/70 tracking-wide">average lead growth</p>
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">Why Levaze</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1014]">Strategy first. Results always.</h2>
            <p className="mt-5 text-lg text-[#5C4B51] leading-relaxed">
              At Levaze, we craft digital solutions that help small and medium businesses thrive — with clear strategies, honest reporting and measurable results.
            </p>
            <ul className="mt-7 space-y-3.5">
              {["Dedicated team for every account", "Transparent monthly reporting", "Built for the Indian SMB market"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#1A1014]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A31621]" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-1.5 mt-8 text-[#6C0B1B] font-medium hover:gap-3 transition-[gap] duration-200">
              Our story <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#F3EFE9] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="mb-14">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-3">Customer reviews</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1014]">What our clients say</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} data-testid={`testimonial-card-${i}`}
                className="bg-[#FDFBF7] border border-[#6C0B1B]/10 rounded-2xl p-8 flex flex-col shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
                <Quote size={26} className="text-[#D4A24C]" />
                <p className="mt-4 text-[15px] text-[#5C4B51] leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex gap-1 mt-5">
                  {[...Array(5)].map((_, s) => <Star key={s} size={14} className="fill-[#D4A24C] text-[#D4A24C]" />)}
                </div>
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#6C0B1B]/10">
                  <img src={t.photo} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-[#1A1014]">{t.name}</p>
                    <p className="text-xs text-[#5C4B51]">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
